import React, { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { animated, useSpring, useTrail, config } from 'react-spring'
import { Swiper, Keyboard } from 'swiper/js/swiper.esm'
import ReactIdSwiperCustom from 'react-id-swiper/lib/ReactIdSwiper.custom'
import BezierEasing from 'bezier-easing'
import { ScrollbarContext } from '../context/scrollbar-context'

import Category from './category'
import Project from './project'
import Title from '../title'
import DownChevronSvg from '../../images/chevron-down.svg'
import DragArrowSvg from '../../images/drag-arrow.svg'

import 'swiper/css/swiper.css'

const List = ({
  projects,
  categories,
  commercialOffices,
  communitySpaces,
  historicRenovations,
  multifamilyResidential,
  restaurantsAndRental
}) => {
  const [scrollbar] = useContext(ScrollbarContext)
  const [categoriesToggle, setCategoriesToggle] = useState(false)
  const [smallFiltersToggle, setSmallFiltersToggle] = useState(false)
  const [currentCategory, setCurrentCategory] = useState('')
  const [projectsToList, setProjectsToList] = useState(projects)
  const [projectClicked, setProjectClicked] = useState(false)
  const [projectWrapperDelay, setProjectWrapperDelay] = useState(false)
  const [swiper, updateSwiper] = useState(null)
  const [render, setRender] = useState(0)

  const filterProjects = category => {
    switch (category) {
      case 'Multifamily Residential':
        setProjectsToList(multifamilyResidential)
        break
      case 'Historic Renovations':
        setProjectsToList(historicRenovations)
        break
      case 'Commercial Offices':
        setProjectsToList(commercialOffices)
        break
      case 'Restaurants and Retail':
        setProjectsToList(restaurantsAndRental)
        break
      case 'Community Spaces':
        setProjectsToList(communitySpaces)
        break
      default:
    }
  }

  const categoriesAnimation = useSpring({
    transform: categoriesToggle ? 'translate3d(-2%, 0, 0)' : 'translate3d(0%, 0, 0)',
    opacity: categoriesToggle ? 0 : 1,
    pointerEvents: categoriesToggle ? 'none' : 'all',
    config: {
      duration: 800,
      easing: categoriesToggle
        ? BezierEasing(0.55, 0.055, 0.675, 0.19)
        : BezierEasing(0.215, 0.61, 0.355, 1)
    },
    delay: categoriesToggle ? 0 : 800
  })

  const currentCategoryTitleAnimation = useSpring({
    opacity: categoriesToggle ? 1 : 0,
    from: {
      opacity: 0
    },
    config: config.slow,
    delay: categoriesToggle ? 1000 : 0
  })

  const dragIndicatorTitleAnimation = useSpring({
    opacity: categoriesToggle ? 1 : 0,
    from: {
      opacity: 0
    },
    config: config.slow,
    delay: categoriesToggle ? 2500 : 0
  })

  const categoryTrail = useTrail(categories.length, {
    to: {
      transform: 'translate3d(0, 0, 0)',
      opacity: 1
    },
    from: {
      transform: 'translate3d(4%, 0, 0)',
      opacity: 0
    },
    config: {
      ...config.default,
      clamp: true
    }
  })

  const projectsContainerAnimation = useSpring({
    opacity: categoriesToggle ? 1 : 0,
    pointerEvents: categoriesToggle ? 'all' : 'none',
    from: {
      opacity: 0,
      pointerEvents: 'none'
    },
    config: {
      duration: 10
    },
    delay: categoriesToggle ? 1000 : 0
  })

  const titleAnimation = useSpring({
    opacity: projectClicked ? 0 : 1,
    from: {
      opacity: 1
    },
    config: {
      duration: 200
    }
  })

  const projectsWrapperAnimation = useSpring({
    opacity: smallFiltersToggle ? 0 : 1,
    transform: projectClicked ? 'translate3d(-4%, 0, 0)' : 'translate3d(0%, 0, 0)',
    from: {
      opacity: 1,
      transform: 'translate3d(0%, 0, 0)'
    },
    config: {
      duration: 400,
      easing: projectClicked
        ? BezierEasing(0.42, 0.0, 1.0, 1.0)
        : BezierEasing(0.215, 0.61, 0.355, 1)
    }
  })

  const smallFiltersAnimation = useSpring({
    opacity: categoriesToggle ? 1 : 0,
    pointerEvents: categoriesToggle ? 'all' : 'none',
    from: {
      opacity: 0,
      pointerEvents: 'none'
    },
    config: config.slow,
    delay: categoriesToggle ? 1500 : 0
  })

  const handleCategoryClick = category => {
    setRender(render + 1)
    setCategoriesToggle(true)
    setCurrentCategory(category)

    if (typeof window !== `undefined`) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    if (scrollbar) {
      scrollbar.scrollTo(0, 0, 300)
    }

    sessionStorage.setItem('category', category)

    setTimeout(() => {
      // The animations mess with the swiper layout spacing so triggering an update
      // just after the animation is happening fixes this
      if (swiper) {
        swiper.update()
      }
    }, 1000)

    // If the categry is all, use the unfiltered list of project
    // Else, run the filtering function and pass the category name
    if (category === 'All') {
      setProjectsToList(projects)
    } else {
      filterProjects(category)
    }
  }

  const handleSmallFilterClick = category => {
    setSmallFiltersToggle(true)

    setTimeout(() => {
      setCurrentCategory(category)
      setSmallFiltersToggle(false)
      setProjectWrapperDelay(true)
      sessionStorage.setItem('category', category)

      if (scrollbar) {
        scrollbar.scrollTo(0, 0, 300)
      } else {
        window.scrollTo(0, 0)
      }

      // If the categry is all, use the unfiltered list of project
      // Else, run the filtering functiona and pass the cateogry name
      if (category === 'All') {
        setProjectsToList(projects)
      } else {
        filterProjects(category)
      }

      setRender(render + 1)

      // The animations mess with the swiper layout spacing so triggering an update
      // just after the animation is happening fixes this
      swiper.update()
    }, 300)

    setTimeout(() => {
      setProjectWrapperDelay(false)
    }, 2000)
  }

  const handleBackClick = () => {
    setCategoriesToggle(false)
    setTimeout(() => {
      // Set our swiper back to the first slide
      if (swiper !== null) {
        swiper.slideTo(0)
      }
    }, 1000)
  }

  useEffect(() => {
    if (sessionStorage.getItem('category')) {
      handleCategoryClick(sessionStorage.getItem('category'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const swiperParams = {
    Swiper,
    modules: [Keyboard],
    keyboard: {
      enabled: true,
      onlyInViewport: false
    },
    slidesPerView: 'auto',
    spaceBetween: 20,
    speed: 1000,
    threshold: 20,
    grabCursor: true,
    centeredSlides: true,
    breakpoints: {
      850: {
        centeredSlides: false,
        spaceBetween: 40
      }
    }
  }

  return (
    <Outer>
      <ListContainer currentstate={categoriesToggle ? 'projects' : 'categories'}>
        <TitleContainer style={titleAnimation}>
          <Title text="Portfolio" type="h1" />
          <animated.div style={projectsWrapperAnimation}>
            <animated.p style={currentCategoryTitleAnimation}>/ {currentCategory}</animated.p>
          </animated.div>
        </TitleContainer>
        <CategoriesContainer
          style={categoriesAnimation}
          currentstate={categoriesToggle ? 'projects' : 'categories'}
        >
          {categoryTrail.map(({ ...rest }, index) => (
            <CategoryWrapper
              onClick={() =>
                handleCategoryClick(
                  categories[index].node.name !== 'Uncategorized'
                    ? categories[index].node.name
                    : 'All'
                )
              }
              key={index}
              style={{ ...rest }}
            >
              <Category
                index={index}
                name={
                  categories[index].node.name !== 'Uncategorized'
                    ? categories[index].node.name
                    : 'View All'
                }
                image={categories[index].node.categoryFields.image}
              />
            </CategoryWrapper>
          ))}
        </CategoriesContainer>
        <ProjectsContainer
          style={projectsContainerAnimation}
          categoriesToggle={categoriesToggle}
          currentstate={categoriesToggle ? 'projects' : 'categories'}
        >
          <ProjectsFilterWrapper style={projectsWrapperAnimation}>
            <ReactIdSwiperCustom {...swiperParams} getSwiper={updateSwiper}>
              {projectsToList.map((project, index) => (
                <div
                  key={
                    typeof project.node !== 'undefined'
                      ? project.node.title + render
                      : project.title + render
                  }
                >
                  <Project
                    index={index}
                    project={typeof project.node !== 'undefined' ? project.node : project}
                    toggle={categoriesToggle}
                    setToggle={setCategoriesToggle}
                    projectWrapperDelay={projectWrapperDelay}
                    setProjectClicked={setProjectClicked}
                  />
                </div>
              ))}
            </ReactIdSwiperCustom>
          </ProjectsFilterWrapper>
        </ProjectsContainer>
        <SmallFilters style={smallFiltersAnimation}>
          <button
            type="button"
            onClick={() => handleSmallFilterClick('All')}
            className={currentCategory === 'All' ? 'active' : ''}
          >
            All
          </button>
          {categories.map((category, index) => (
            <React.Fragment key={index}>
              {categories[index].node.name !== 'Uncategorized' && (
                <button
                  key={index}
                  type="button"
                  className={category.node.name === currentCategory ? 'active' : ''}
                  onClick={() => handleSmallFilterClick(categories[index].node.name)}
                >
                  {category.node.name}
                </button>
              )}
            </React.Fragment>
          ))}
        </SmallFilters>
        <MobileFilters
          onChange={e => handleSmallFilterClick(e.target.value)}
          categoriesToggle={categoriesToggle}
          style={smallFiltersAnimation}
        >
          <option value="All" label="View all">
            View all
          </option>
          {categories.map((category, index) => (
            <React.Fragment key={index}>
              {categories[index].node.name !== 'Uncategorized' && (
                <option value={categories[index].node.name} label={categories[index].node.name}>
                  {categories[index].node.name}
                </option>
              )}
            </React.Fragment>
          ))}
        </MobileFilters>
        <DragPrompt style={dragIndicatorTitleAnimation}>
          {projectsToList.length > 2 && (
            <>
              <span>Drag for more projects</span>
              <DragArrowSvg />
            </>
          )}
        </DragPrompt>
        <Back onClick={() => handleBackClick()} style={projectsContainerAnimation}>
          <p>Back</p>
          <DownChevronSvg />
        </Back>
      </ListContainer>
    </Outer>
  )
}

export default List

const Outer = styled.section`
  position: relative;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  background-color: ${props => props.theme.colours.darkGrey};

  @media (max-width: 850px) {
    flex-direction: column;
    height: auto;
  }
`

const ListContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  width: 84vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  margin-left: 8%;
  padding: 7% 0;

  @media (max-width: 850px) {
    position: relative;
    flex-direction: column;
    justify-content: flex-start;
    height: auto;
    transition: max-height 2s ease;
    padding: 25% 0 7%;
  }
`

const TitleContainer = styled(animated.aside)`
  position: absolute;
  top: 17%;
  left: 1%;
  pointer-events: none;
  z-index: 10;

  @media (max-width: 850px) {
    position: relative;
    top: initial;
    left: initial;
  }

  h1 {
    font-size: 7rem;
    line-height: 1.2;
    margin-bottom: 0;
  }

  p {
    font-family: ${props => props.theme.fonts.maison};
    font-weight: 400;
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 0.4rem;
    line-height: 1.4;
    color: #fff;

    @media (max-width: 1000px) {
      font-size: 1.8rem;
    }
  }
`

const CategoriesContainer = styled(animated.nav)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 90%;

  @media (max-width: 1400px) {
    width: 94%;
  }

  @media (max-width: 850px) {
    width: 100%;
    max-height: ${props => (props.currentstate === 'projects' ? '0' : '99999px')};
    transition: ${props =>
      props.currentstate === 'projects' ? 'max-height 0s linear 1s' : 'max-height 0s linear 0s'};
  }

  p {
    color: #fff;
  }

  &:hover {
    > * > * {
      opacity: 0.5;
    }
  }

  > * > * {
    transition: opacity 1s ease;

    &:hover {
      opacity: 1;
    }
  }
`

const CategoryWrapper = styled(animated.div)`
  position: relative;
  width: calc(33.33% - 2rem);
  height: calc(50% - 2rem);
  margin: 0 1rem;
  cursor: pointer;

  @media (max-width: 850px) {
    width: 100%;
    margin: 0 0 3rem;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: '';
      display: block;
      padding-bottom: 80%;
    }
  }
`

const ProjectsContainer = styled(animated.section)`
  position: absolute;
  right: -8vw;
  top: 50%;
  width: 85vw;
  height: 55%;
  transform: translate3d(0, -50%, 0);
  transition: opacity 0.6s ease;
  will-change: transform, opacity;

  @media (max-width: 1700px) {
    height: 63.5%;
  }

  @media (max-width: 850px) {
    position: relative;
    top: 24%;
    left: -30px;
    width: calc(100% + 60px);
    height: 50%;
    transform: translate3d(0, 0, 0);
    right: initial;
    max-height: ${props => (props.currentstate === 'projects' ? '99999px' : '0')};
    transition: max-height 0 linear 1s;
  }

  .swiper-container {
    height: 100%;
  }

  .swiper-slide {
    width: 38%;
    transition: opacity 1s ease;

    @media (max-width: 850px) {
      width: 80%;

      &::before {
        content: '';
        display: block;
        margin-bottom: 120%;
      }
    }
  }

  .swiper-slide-active {
    @media (max-width: 850px) {
      .gatsby-image-wrapper {
        opacity: 1;
      }
    }
  }

  .swiper-slide-prev,
  .swiper-slide-next,
  .swiper-slide-duplicate-prev,
  .swiper-slide-duplicate-next {
    @media (max-width: 850px) {
      opacity: 0.26;
    }
  }
`

const ProjectsFilterWrapper = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  @media (max-width: 850px) {
    position: relative;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
`

const Back = styled(animated.button)`
  position: absolute;
  top: 50%;
  left: 2rem;
  transform: translate(-9rem, -50%) rotate(90deg);
  transform-origin: bottom left;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  z-index: 10;

  @media (max-width: 850px) {
    position: relative;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: row-reverse;
    transform: translate(0, 0) rotate(0);
    margin-top: 2rem;
  }

  &:focus {
    outline: none;
  }

  svg {
    position: relative;
    width: 1.5rem;
    left: -0.25rem;

    @media (max-width: 850px) {
      top: 0.3rem;
      transform-origin: center;
      transform: rotate(90deg);
      margin-right: 10px;
      width: 16px;
    }
  }

  p {
    font-family: ${props => props.theme.fonts.maison};
    font-weight: 400;
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 0.4rem;
    line-height: 1.4;
    color: #fff;
    margin-bottom: 0.8rem;

    @media (max-width: 850px) {
      font-size: 1.8rem;
    }
  }
`

const SmallFilters = styled(animated.nav)`
  position: absolute;
  width: 100vw;
  padding: 0 3rem;
  bottom: 10%;
  left: -8vw;
  right: 0;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 850px) {
    display: none;
  }

  button {
    font-family: ${props => props.theme.fonts.maison};
    font-weight: 400;
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    line-height: 1.4;
    color: #6c6c6c;
    background: none;
    border: none;
    margin: 0 1rem;
    padding: 0;
    transition: color 1s ease;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &:hover {
      color: #fff;
    }

    &.active {
      color: ${props => props.theme.colours.orange};
      border-bottom: 1px solid ${props => props.theme.colours.orange};
    }
  }
`

const DragPrompt = styled(animated.aside)`
  position: absolute;
  display: flex;
  align-items: center;
  top: 28%;
  right: 8%;
  z-index: 10;

  @media (max-width: 850px) {
    display: none;
  }

  span {
    font-family: ${props => props.theme.fonts.maison};
    font-weight: 400;
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 0.4rem;
    line-height: 1.4;
    color: #fff;
    margin-right: 1rem;
  }

  svg {
    position: relative;
    top: -0.2rem;
    width: 4.5rem;
  }
`

const MobileFilters = styled(animated.select)`
  display: none;
  position: relative;
  top: 75%;
  left: 0;
  width: 100%;
  font-family: ${props => props.theme.fonts.maison};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.4rem;
  font-size: 1.8rem;
  line-height: 1.6;
  text-align-last: center;
  color: #fff;
  line-height: 1.3;
  padding: 0.6em 1.4em 1em 0.8em;
  width: 80%;
  max-width: 40rem;
  box-sizing: border-box;
  margin: 3rem auto;
  border: 0;
  border-bottom: 1px solid ${props => props.theme.colours.orange};
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  border-radius: 0;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMCIgaGVpZ2h0PSI5IiB2aWV3Qm94PSIwIDAgMTAgOSI+CiAgPHBhdGggaWQ9IlBvbHlnb25fMSIgZGF0YS1uYW1lPSJQb2x5Z29uIDEiIGQ9Ik01LDBsNSw5SDBaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxMCA5KSByb3RhdGUoMTgwKSIgZmlsbD0iI2Q1NjUxYiIvPgo8L3N2Zz4K');
  background-repeat: no-repeat, repeat;
  background-position: right 1.6em top 35%, 0 0;
  background-size: 0.8em auto, 100%;
  overflow: hidden;

  @media (max-width: 850px) {
    display: ${props => (props.categoriesToggle ? 'block' : 'none')};
  }

  @media (max-width: 400px) {
    font-size: 16px;
  }

  @media (max-width: 400px) {
    width: 100%;
    max-width: 100%;
  }

  &::-ms-expand {
    display: none;
  }

  &:focus {
    outline: none;
  }
`
